<template>
  <div>
    <v-text-field
      class="searchable-select"
      variant="outlined"
      :density="density"
      :hideDetails="hideDetails"
      :hint="hint"
      :persistent-hint="persistentHint"
      readonly
      :label="label"
      :append-inner-icon="open ? 'mdi-menu-up' : 'mdi-menu-down'"
      @click:clear="clearSelection"
      :validation-value="selectedItems"
      :disabled="disabled"
      :rules="rules"
      :loading="loading"
      :clearable="clearable"
    >
      <div
        v-if="selectedText && !$slots.selection"
        :class="wrapSelected ? 'yes-wrap' : 'no-wrap'"
      >
        {{ selectedText }}
      </div>

      <div v-else-if="!$slots.selection" class="hidden-text">{{ label }}</div>

      <!-- Custom selection slot -->
      <div v-if="$slots.selection" class="selection-container">
        <template v-if="multiple && selectedItemsForSlot?.length">
          <slot
            name="selection"
            v-for="(item, index) in selectedItemsForSlot"
            :key="getItemValue(item)"
            :item="item"
            :index="index"
          ></slot>
        </template>

        <template v-else-if="!multiple && selectedItemsForSlot">
          <slot name="selection" :item="selectedItemsForSlot" :index="0"></slot>
        </template>
      </div>

      <div v-else-if="!selectedText" class="hidden-text">{{ label }}</div>

      <v-menu
        v-model="open"
        :close-on-content-click="false"
        :persistent="persistentMenu"
        activator="parent"
      >
        <v-list :density="density" select-strategy="independent" ref="listRef">
          <v-text-field
            class="search-container"
            v-model="searchComputed"
            :placeholder="t('search')"
            hide-details
            :density="density"
            clearable
            autofocus
            :onKeydown="onKeydown"
          />

          <v-list-item
            @click="toggleAll()"
            v-if="multiple && !disableSelectAll && !readonly"
          >
            <v-icon>
              {{ icon }}
            </v-icon>
            {{ selectedItems.length === items.length ? t('clear') : t('all') }}
          </v-list-item>

          <v-divider />

          <v-virtual-scroll
            :items="filteredItems"
            :item-height="20"
            :max-height="300"
            :key="search"
          >
            <template v-slot="{ item }">
              <slot
                name="item"
                :item="item"
                :props="{
                  key: getItemKey(item),
                  onClick: readonly ? null : () => toggleItem(item),
                  active: isSelected(item),
                  title: getItemTitle(item),
                  onkeydown: stopEnterFromClosingMenu,
                  class: readonly ? 'disableItem' : '',
                  ...getItemProps(item),
                }"
                :isSelected="isSelected(item)"
              >
                <v-list-item
                  :key="getItemKey(item)"
                  @click="readonly ? null : toggleItem(item)"
                  :active="isSelected(item)"
                  :title="getItemTitle(item)"
                  v-bind="getItemProps(item)"
                  :onkeydown="stopEnterFromClosingMenu"
                  :class="readonly ? 'disableItem' : ''"
                >
                  <template v-slot:prepend>
                    <v-checkbox-btn
                      v-if="multiple"
                      :model-value="selectedItems"
                      :value="returnObject ? item : getItemValue(item)"
                      :readonly="readonly"
                      hide-details
                      density="compact"
                      :ripple="false"
                      :tabindex="-1"
                    ></v-checkbox-btn>
                  </template>
                </v-list-item>
              </slot>
            </template>
          </v-virtual-scroll>

          <slot name="footer"></slot>
        </v-list>
      </v-menu>
    </v-text-field>
  </div>
</template>

<script setup>
import { ref, computed, toRefs, watch } from 'vue';
import debounce from 'lodash/debounce';
import { useI18n } from 'vue-i18n';
import { isArray, isRecord, isString } from 'tcheck';

const { t } = useI18n();

//https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/components/VSelect/VSelect.tsx
const props = defineProps({
  label: {
    type: String,
    default: 'Items',
  },
  items: {
    type: Array,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  itemTitle: {
    type: String,
    default: 'title',
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  customToggleAllFn: {
    type: Function,
  },
  disableSelectAll: {
    type: Boolean,
    default: false,
  },
  returnObject: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  density: {
    type: String,
    default: 'compact',
  },
  getItemProps: {
    type: Function,
    default: () => ({}),
  },
  hint: {
    type: String,
  },
  persistentHint: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  customFilter: {
    type: Function,
  },
  persistentMenu: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  wrapSelected: {
    type: Boolean,
    default: false,
  },
  showSelectedAtTop: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  showItemTotalOnSelectAll: {
    type: Boolean,
    default: false,
  },
});

const selectedItems = defineModel();
const {
  label,
  items,
  multiple,
  itemTitle,
  itemValue,
  customToggleAllFn,
  disableSelectAll,
  returnObject,
  hideDetails,
  hint,
  persistentHint,
  density,
  getItemProps,
  disabled,
  rules,
  loading,
  customFilter,
  persistentMenu,
  wrapSelected,
  showSelectedAtTop,
  readonly,
  showItemTotalOnSelectAll,
} = toRefs(props);

function getItemTitle(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  // if itemTitle has a dot, it means it's a nested property
  if (itemTitle.value.includes('.')) {
    const props = itemTitle.value.split('.');
    return props.reduce((acc, prop) => acc[prop], item);
  }
  return item[itemTitle.value];
}

function getItemValue(item) {
  if (!item) return '';
  if (isString(item)) {
    return item;
  }
  return item[itemValue.value];
}

function getItemKey(item) {
  if (isString(item)) {
    return item;
  }

  if (isRecord(item) && isString(item.name)) {
    return item.name;
  }

  if (isRecord(item) || isArray(item)) {
    return JSON.stringify(item);
  }

  return `${item}`;
}

const open = defineModel('open', { required: false, default: false });

const search = ref(null);
watch(open, () => {
  search.value = null;
});

const searchComputed = computed({
  get() {
    return search.value;
  },
  set: debounce((v) => {
    search.value = v;
  }, 200),
});

const isSelectAll = computed(() => {
  return selectedItems.value?.length === items.value.length;
});

const icon = computed(() => {
  if (isSelectAll.value) return 'mdi-close-box';
  if (selectedItems.value?.length > 0) return 'mdi-minus-box';
  return 'mdi-checkbox-blank-outline';
});

const filteredItems = computed(() => {
  if (!search.value) {
    if (showSelectedAtTop.value) {
      const newItems = [...items.value];
      return newItems.sort((a, b) => {
        const aSelected = isSelected(a) ? 1 : 0;
        const bSelected = isSelected(b) ? 1 : 0;
        return bSelected - aSelected;
      });
    }
    return items.value;
  }
  if (customFilter.value)
    return items.value.filter((item) => customFilter.value(search.value, item));
  return items.value.filter((x) =>
    String(getItemTitle(x))
      ?.toLowerCase()
      ?.includes(String(search.value)?.toLowerCase()),
  );
});

// Build a value-to-item map for efficient lookups
const valueToItemMap = computed(() => {
  const map = new Map();
  for (const item of items.value) {
    map.set(getItemValue(item), item);
  }
  return map;
});

// Computed properties for slot data
const selectedItemsForSlot = computed(() => {
  if (!multiple.value) {
    if (!selectedItems.value) return null;
    if (returnObject.value) return selectedItems.value;
    return valueToItemMap.value.get(selectedItems.value) || null;
  }
  if (!selectedItems.value?.length) return [];
  if (returnObject.value) {
    return selectedItems.value;
  } else {
    return selectedItems.value
      .map((value) => valueToItemMap.value.get(value))
      .filter((item) => item !== undefined);
  }
});

function isSelected(item) {
  if (multiple.value) {
    if (returnObject.value) {
      return selectedItems.value?.some(
        (selectedItem) => JSON.stringify(selectedItem) === JSON.stringify(item),
      );
    } else {
      return selectedItems.value?.includes(getItemValue(item));
    }
  } else {
    if (returnObject.value) {
      return JSON.stringify(selectedItems.value) === JSON.stringify(item);
    } else {
      return selectedItems.value === getItemValue(item);
    }
  }
}

function toggleItem(item) {
  if (multiple.value) {
    if (returnObject.value) {
      const stringifiedItem = JSON.stringify(item);
      if (
        selectedItems.value?.some((x) => JSON.stringify(x) === stringifiedItem)
      ) {
        selectedItems.value = selectedItems.value.filter(
          (x) => JSON.stringify(x) !== stringifiedItem,
        );
      } else {
        if (!selectedItems.value) selectedItems.value = [];
        selectedItems.value = [...selectedItems.value, item];
      }
    } else {
      if (selectedItems.value?.includes(getItemValue(item)))
        selectedItems.value = selectedItems.value.filter(
          (x) => x !== getItemValue(item),
        );
      // else selectedItems.value.push(getItemValue(item));
      else {
        if (!selectedItems.value) selectedItems.value = [];
        selectedItems.value = [...selectedItems.value, getItemValue(item)];
      }
    }
  } else {
    if (returnObject.value) {
      selectedItems.value = item;
    } else {
      selectedItems.value = getItemValue(item);
    }
    open.value = false;
  }
}

function toggleAll() {
  if (customToggleAllFn.value) customToggleAllFn.value();
  else if (selectedItems.value?.length === items.value.length)
    selectedItems.value = [];
  else if (returnObject.value) selectedItems.value = items.value;
  else selectedItems.value = items.value.map((x) => getItemValue(x));
}

const selectedText = computed(() => {
  if (!multiple.value) {
    if (!selectedItems.value) return '';
    if (returnObject.value) return getItemTitle(selectedItems.value);
    const obj = valueToItemMap.value.get(selectedItems.value);
    return getItemTitle(obj);
  }
  if (!selectedItems.value?.length) return '';
  if (
    selectedItems.value?.length > 1 &&
    selectedItems.value?.length === items.value.length
  )
    return showItemTotalOnSelectAll.value
      ? `${t('all')} (${selectedItems.value.length})`
      : t('all');
  if (selectedItems.value?.length > 1)
    return `${selectedItems.value?.length} ${label.value}`;
  if (returnObject.value) return getItemTitle(selectedItems.value[0]);
  const obj = valueToItemMap.value.get(selectedItems.value[0]);
  return getItemTitle(obj);
});

function clearSelection() {
  if (multiple.value) selectedItems.value = [];
  else selectedItems.value = null;
}

watch(
  multiple,
  () => {
    if (multiple.value) {
      if (!selectedItems.value) selectedItems.value = [];
    }
  },
  { immediate: true },
);

const listRef = ref();
function onKeydown(e) {
  if (!e.key) return;
  if (e.key === 'Enter') {
    e.stopPropagation();
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    listRef.value?.focus('next');
  }
}

function stopEnterFromClosingMenu(e) {
  if (e.key === 'Enter') {
    e.stopPropagation();
  }
}
</script>
<i18n>
{
  "en": {
    "search": "Search",
    "clear": "Clear",
    "all": "All",
  },
  "es": {
    "search": "Buscar",
    "clear": "Borrar",
    "all": "Todos",
  }
}
</i18n>
<style scoped lang="scss">
.search-container {
  position: sticky;
  top: 0px;
  z-index: 1;
  background: rgb(var(--v-theme-background));
}

.no-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.yes-wrap {
  white-space: normal;
}

.v-list-item-title {
  white-space: unset;
}

.disableItem {
  cursor: default;
  color: #a3a3a3;
}

.searchable-select {
  height: fit-content;
}

.hidden-text {
  visibility: hidden;
}
:deep(.v-field__input) {
  input {
    align-self: flex-start;
    opacity: 1;
    flex: 0 0;
    position: absolute;
    width: 100%;
    transition: none;
    pointer-events: none;
    caret-color: transparent;
  }
}
.selection-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
